// System (plan step 2.4; D59): Apsio's own services from PostHog, without opening it. The error
// groups of the last 7 days (most frequent first, refreshed every minute), and a search of the
// services' logs by service, level, request id and hours back, kept in the address so a search
// can be shared. Apsio's own errors and logs only, never customer telemetry. Console D (APS-412,
// admin-d.md E): tables in the console's `.t`, the search in one Logs-style row of fields.
import { PageHead, Section } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useId, useState } from 'react'
import { errorGroupsQuery, type LogSearch, logsQuery, posthogAlertsQuery } from '../api/queries'
import { DataTable, IdText } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { PostHogSource } from './posthog'
import { ReplayApprovalPanel } from './ReplayApproval'

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const
export type LogLevel = (typeof LOG_LEVELS)[number]
/** What the API accepts in a service or request id: a plain label, never a pattern. */
export const LABEL = /^[A-Za-z0-9_.:/-]{1,100}$/
export const HOURS = [1, 6, 24, 72, 168] as const

/** The address's search, kept to what the API accepts. */
export function logSearch(s: Record<string, unknown>): LogSearch {
  const out: LogSearch = {}
  if (typeof s.service === 'string' && LABEL.test(s.service)) out.service = s.service
  if (LOG_LEVELS.includes(s.level as LogLevel)) out.level = s.level as LogLevel
  if (typeof s.request_id === 'string' && LABEL.test(s.request_id)) out.request_id = s.request_id
  const hours = Number(s.hours)
  if (HOURS.includes(hours as (typeof HOURS)[number])) out.hours = hours
  return out
}

/** PostHog's alerts that need a look (D59): firing first, then those whose checks fail. */
function Alerts() {
  const { t, dateTime } = useI18n()
  const alerts = useQuery(posthogAlertsQuery)
  return (
    <Section title={t('system.alertsTitle')} meta={t('system.alertsSub')}>
      {alerts.isPending ? (
        <Loading />
      ) : alerts.isError ? (
        <ErrorState error={alerts.error} retry={() => alerts.refetch()} />
      ) : (
        <PostHogSource section={alerts.data}>
          {alerts.data.data && alerts.data.data.length > 0 ? (
            <DataTable label={t('table.of', { name: t('system.alertsTitle') })} minWidth={560}>
              <thead>
                <tr>
                  <th scope="col" className="f">
                    {t('system.col.alert')}
                  </th>
                  <th scope="col">{t('system.col.state')}</th>
                  <th scope="col" className="num">
                    {t('system.col.value')}
                  </th>
                  <th scope="col" className="last">
                    {t('system.col.checked')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {alerts.data.data.map((a) => (
                  <tr key={a.id}>
                    <td className="f">{a.name}</td>
                    <td>
                      {t(
                        a.state === 'Firing'
                          ? 'system.alertFiring'
                          : a.state === 'Broken'
                            ? 'system.alertBroken'
                            : 'system.alertErrored',
                      )}
                    </td>
                    <td className="num">{a.last_value ?? '—'}</td>
                    <td className="last">
                      {a.last_checked_at ? dateTime(a.last_checked_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <Empty title={t('system.noAlerts')} />
          )}
        </PostHogSource>
      )}
    </Section>
  )
}

function ErrorGroups() {
  const { t, number, dateTime } = useI18n()
  const groups = useQuery(errorGroupsQuery)
  return (
    <Section title={t('system.errorsTitle')} meta={t('system.errorsSub')}>
      {groups.isPending ? (
        <Loading />
      ) : groups.isError ? (
        <ErrorState error={groups.error} retry={() => groups.refetch()} />
      ) : (
        <PostHogSource section={groups.data}>
          {groups.data.data && groups.data.data.length > 0 ? (
            <DataTable label={t('table.of', { name: t('system.errorsTitle') })} minWidth={640}>
              <thead>
                <tr>
                  <th scope="col" className="f">
                    {t('system.col.type')}
                  </th>
                  <th scope="col">{t('system.col.service')}</th>
                  <th scope="col">{t('system.col.release')}</th>
                  <th scope="col" className="num">
                    {t('system.col.count')}
                  </th>
                  <th scope="col" className="last">
                    {t('system.col.lastSeen')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.data.data.map((g) => (
                  <tr key={g.fingerprint}>
                    <td className="f" translate="no">
                      {g.type || g.fingerprint}
                    </td>
                    <td translate="no">{g.service}</td>
                    <td>
                      <IdText id={g.release} head={8} tail={4} />
                    </td>
                    <td className="num">{number(g.count)}</td>
                    <td className="last">{dateTime(g.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <Empty title={t('system.noErrors')} />
          )}
        </PostHogSource>
      )}
    </Section>
  )
}

function Logs({ search }: { search: LogSearch }) {
  const { t, dateTime } = useI18n()
  const navigate = useNavigate()
  const [service, setService] = useState(search.service ?? '')
  const [requestId, setRequestId] = useState(search.request_id ?? '')
  const ids = { service: useId(), level: useId(), request: useId(), hours: useId() }
  const lines = useQuery(logsQuery(search))

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    navigate({
      to: '/system',
      search: logSearch({
        service: service.trim(),
        request_id: requestId.trim(),
        level: form.get('level'),
        hours: form.get('hours'),
      }),
    })
  }

  return (
    <Section title={t('system.logsTitle')} meta={t('system.logsSub')}>
      <search>
        <form className="ops-filters" onSubmit={submit} aria-label={t('system.searchForm')}>
          <div className="field-d">
            <label className="lbl" htmlFor={ids.service}>
              {t('system.service')}
            </label>
            <input
              id={ids.service}
              className="input mono"
              value={service}
              onChange={(e) => setService(e.target.value)}
              pattern={LABEL.source.slice(1, -1)}
              maxLength={100}
              autoComplete="off"
              spellCheck={false}
              translate="no"
            />
          </div>
          <div className="field-d">
            <label className="lbl" htmlFor={ids.level}>
              {t('system.level')}
            </label>
            <select
              id={ids.level}
              name="level"
              className="select"
              defaultValue={search.level ?? ''}
            >
              <option value="">{t('system.anyLevel')}</option>
              {LOG_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {t(`system.level.${l}` as MessageId)}
                </option>
              ))}
            </select>
          </div>
          <div className="field-d">
            <label className="lbl" htmlFor={ids.request}>
              {t('system.requestId')}
            </label>
            <input
              id={ids.request}
              className="input mono"
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
              pattern={LABEL.source.slice(1, -1)}
              maxLength={100}
              autoComplete="off"
              spellCheck={false}
              translate="no"
            />
          </div>
          <div className="field-d">
            <label className="lbl" htmlFor={ids.hours}>
              {t('system.hours')}
            </label>
            <select
              id={ids.hours}
              name="hours"
              className="select"
              defaultValue={String(search.hours ?? 24)}
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {t('system.hoursValue', { n: h })}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn solid">
            {t('system.search')}
          </button>
        </form>
      </search>
      {lines.isPending ? (
        <Loading />
      ) : lines.isError ? (
        <ErrorState error={lines.error} retry={() => lines.refetch()} />
      ) : (
        <PostHogSource section={lines.data}>
          {lines.data.data && lines.data.data.length > 0 ? (
            <DataTable
              label={t('table.of', { name: t('system.logsTitle') })}
              minWidth={760}
              className="ops-logs"
            >
              <thead>
                <tr>
                  <th scope="col" className="f">
                    {t('system.col.at')}
                  </th>
                  <th scope="col">{t('system.col.level')}</th>
                  <th scope="col">{t('system.col.service')}</th>
                  <th scope="col">{t('system.col.message')}</th>
                  <th scope="col" className="last">
                    {t('system.col.request')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.data.data.map((l, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: log lines have no id; the API's order
                  <tr key={i}>
                    <td className="f">{dateTime(l.at)}</td>
                    <td translate="no" className={`lv ${l.level}`}>
                      {l.level}
                    </td>
                    <td translate="no">{l.service}</td>
                    <td className="wrap msg" translate="no">
                      {l.message}
                    </td>
                    <td className="last">
                      {l.request_id ? <IdText id={l.request_id} /> : t('common.none')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <Empty title={t('system.noLogs')} />
          )}
        </PostHogSource>
      )}
    </Section>
  )
}

export function SystemPage() {
  const { t } = useI18n()
  const search = logSearch(useSearch({ strict: false }) as Record<string, unknown>)
  return (
    <main id="main" className="pg ops">
      <PageHead title={t('system.title')} meta={t('system.lede')} />
      <Alerts />
      <ErrorGroups />
      <Logs search={search} />
      <ReplayApprovalPanel />
    </main>
  )
}
